/**
 * AddBondModal - Quick add modal for creating bonds
 *
 * Simple interface for rapid bond creation:
 * - Tax Year (required): Starting tax year
 * - Duration (required): Years until final maturity
 * - Target Principal (optional): Dollar target (blank = max capacity)
 *
 * Issue date, interest rates, and payment dates are auto-calculated.
 * For advanced customization, use the "Details" button to open full configuration.
 */

import { Box, Button, Dialog, Field, Input, Stack, Text, VStack } from '@chakra-ui/react';
import type React from 'react';
import { useEffect, useState } from 'react';

import { RealTimeCurrencyInput } from '~/components/inputs';
import type { ProjectData } from '~/hooks/useProjectStore';

export interface QuickBondInput {
  /** Starting tax year for bond payments */
  startingTaxYear: number;

  /** Bond term in years */
  term: number;

  /** Optional target amount in dollars */
  targetAmount?: number;
}

export interface AddBondModalProps {
  /** Whether the modal is open */
  isOpen: boolean;

  /** Handler called when modal should close */
  onClose: () => void;

  /** Handler called when bond is added via quick add */
  onAddBond: (bondData: QuickBondInput) => void;

  /** Handler called when user wants to open details modal */
  onOpenDetails: (initialData: QuickBondInput) => void;

  /** Project data for context */
  project: ProjectData;
}

export const AddBondModal: React.FC<AddBondModalProps> = ({
  isOpen,
  onClose,
  onAddBond,
  onOpenDetails,
}) => {
  const [startingTaxYear, setStartingTaxYear] = useState<string>('');
  const [term, setTerm] = useState<string>('');
  const [targetAmount, setTargetAmount] = useState<string>('');
  const [errors, setErrors] = useState<string[]>([]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setStartingTaxYear('');
      setTerm('');
      setTargetAmount('');
      setErrors([]);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    const newErrors: string[] = [];

    // Validate starting tax year
    if (!startingTaxYear.trim()) {
      newErrors.push('Tax year is required');
    } else {
      const taxYear = parseInt(startingTaxYear, 10);
      if (Number.isNaN(taxYear) || taxYear < 2000 || taxYear > 2100) {
        newErrors.push('Tax year must be between 2000 and 2100');
      }
    }

    // Validate term
    if (!term.trim()) {
      newErrors.push('Duration is required');
    } else {
      const termNum = parseInt(term, 10);
      if (Number.isNaN(termNum) || termNum < 1 || termNum > 40) {
        newErrors.push('Duration must be between 1 and 40 years');
      }
    }

    // Validate target amount (optional)
    if (targetAmount.trim()) {
      const amountNum = parseFloat(targetAmount);
      if (Number.isNaN(amountNum) || amountNum <= 0) {
        newErrors.push('Target principal must be a positive number');
      }
    }

    if (newErrors.length > 0) {
      setErrors(newErrors);
      return;
    }

    // Prepare bond data (simple - issue date and interest rates auto-calculated)
    const bondData: QuickBondInput = {
      startingTaxYear: parseInt(startingTaxYear, 10),
      term: parseInt(term, 10),
      targetAmount: targetAmount.trim() ? parseFloat(targetAmount) : undefined,
    };

    // Call handler
    onAddBond(bondData);

    // Close modal
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(e) => (!e.open ? onClose() : undefined)}
      placement="center"
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content maxWidth="500px">
          <Dialog.Header>
            <Dialog.Title>Add Bond</Dialog.Title>
          </Dialog.Header>

          <Dialog.Body>
            <form onSubmit={handleSubmit}>
              <VStack align="stretch" gap={4}>
                {/* Error messages */}
                {errors.length > 0 && (
                  <Box p={3} bg="red.50" borderWidth="1px" borderColor="red.200" borderRadius="md">
                    <VStack align="stretch" gap={1}>
                      {errors.map((error) => (
                        <Text key={error} fontSize="sm" color="red.600">
                          {error}
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* Tax Year (required) */}
                <Field.Root required>
                  <Field.Label>Tax Year</Field.Label>
                  <Input
                    type="number"
                    placeholder="2025"
                    value={startingTaxYear}
                    onChange={(e) => setStartingTaxYear(e.target.value)}
                  />
                  <Field.HelperText>First tax year for bond payments</Field.HelperText>
                </Field.Root>

                {/* Duration (required) */}
                <Field.Root required>
                  <Field.Label>Duration (Years)</Field.Label>
                  <Input
                    type="number"
                    placeholder="20"
                    min={1}
                    max={40}
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                  />
                  <Field.HelperText>Years until final maturity</Field.HelperText>
                </Field.Root>

                {/* Target Principal (optional) */}
                <RealTimeCurrencyInput
                  label="Target Principal (Optional)"
                  value={targetAmount}
                  onChange={setTargetAmount}
                  helperText="Leave blank for maximum capacity"
                />
              </VStack>
            </form>
          </Dialog.Body>

          <Dialog.Footer>
            <Stack direction="row" gap={2} justify="space-between" width="100%">
              <Button variant="ghost" onClick={handleCancel}>
                Cancel
              </Button>
              <Stack direction="row" gap={2}>
                <Button
                  variant="outline"
                  onClick={() => {
                    // Validate before opening details
                    if (!startingTaxYear.trim() || !term.trim()) {
                      setErrors(['Please fill in tax year and duration before opening details']);
                      return;
                    }
                    const taxYear = parseInt(startingTaxYear, 10);
                    const termNum = parseInt(term, 10);
                    if (
                      Number.isNaN(taxYear) ||
                      taxYear < 2000 ||
                      taxYear > 2100 ||
                      Number.isNaN(termNum) ||
                      termNum < 1 ||
                      termNum > 40
                    ) {
                      setErrors([
                        'Please enter valid tax year and duration before opening details',
                      ]);
                      return;
                    }
                    const quickData: QuickBondInput = {
                      startingTaxYear: taxYear,
                      term: termNum,
                      targetAmount: targetAmount.trim() ? parseFloat(targetAmount) : undefined,
                    };
                    onOpenDetails(quickData);
                  }}
                >
                  Details
                </Button>
                <Button colorPalette="blue" onClick={handleSubmit}>
                  Add Bond
                </Button>
              </Stack>
            </Stack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};
